import React, { memo, useState } from 'react'
import {
  ETierKey,
  ITier,
  statesArray,
} from '../../pages/subscribe/_data/subscribe-constant'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import StripePaymentLayout from '@/pages/subscribe/_component/stripe-payment-layout'
import { subscribeFormSchema } from '@/pages/subscribe/_data/subscribe-definitions'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { parseProfileResponse, useAuth } from '@/hooks/getAuth'
import { z } from 'zod'
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { callAPI } from '@/hooks/api'
import { Separator } from '../ui/separator'
import { Input } from '../ui/input'
import { useNavigate } from 'react-router'
import {
  FormControl,
  FormField,
  FormLabel,
  FormItem,
  FormMessage,
} from '../ui/form'
import { Form } from '../ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { Checkbox } from '../ui/checkbox'
import { Button } from '../ui/button'
import { EFreeTrialStatusType } from '../welcome-modal/welcome-modal'
import { TierCard } from './trial-plans-modal'
import { ArrowLeft } from 'lucide-react'

interface Props {
  tier: ITier | undefined
  tierKey: ETierKey | undefined
  isAnnual: boolean
  isDiscount?: boolean
  onClose: () => void
  onPaymentSuccess: () => void
}

function TrialSubscribeModal({
  tier,
  tierKey,
  onClose,
  isDiscount,
  isAnnual,
  onPaymentSuccess,
}: Props) {
  return (
    <Dialog open={Boolean(tier)} onOpenChange={onClose}>
      <DialogContent
        showClose={true}
        className=" w-full max-w-4xl max-h-[80vh] md:gap-6 overflow-scroll [&::-webkit-scrollbar]:hidden scrollbar-none"
      >
        <DialogHeader>
          <DialogTitle className="md:text-[32px] flex items-center gap-4">
            <ArrowLeft className="h-6 w-6 cursor-pointer" onClick={onClose} />
            Subscription
          </DialogTitle>
        </DialogHeader>
        <StripePaymentLayout>
          <TrialSubscribeContent
            tier={tier}
            tierKey={tierKey}
            onClose={onClose}
            isDiscount={isDiscount}
            isAnnual={isAnnual}
            onPaymentSuccess={onPaymentSuccess}
          />
        </StripePaymentLayout>
      </DialogContent>
    </Dialog>
  )
}

export default memo(TrialSubscribeModal)

const TrialSubscribeContent = ({
  tier,
  isDiscount,
  tierKey,
  isAnnual,
  onPaymentSuccess,
}: Props) => {
  const { auth, setAuth, setIsLoggedIn } = useAuth()
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  // Debug priceID
  // const priceId = isDiscount ? tier?.price_discount_id : tier?.price_id
  // console.log(priceId)

  const form = useForm<z.infer<typeof subscribeFormSchema>>({
    resolver: zodResolver(subscribeFormSchema),
    defaultValues: {
      terms: false,
      firstName: auth.firstname,
      lastName: auth.lastname,
      email: auth.email,
    },
  })

  const saveLocalStorage = () => {
    let localAuth = localStorage.getItem('auth')
    let _tierKey = tierKey

    if (isAnnual) {
      if (tierKey === ETierKey.Advanced) {
        _tierKey = ETierKey.AdvancedAnnual
      } else if (tierKey === ETierKey.Professional) {
        _tierKey = ETierKey.ProfessionalAnnual
      }
    }
    if (localAuth) {
      let authObject = JSON.parse(localAuth)
      authObject.status = 'success'
      authObject.tier = _tierKey
      authObject.trial_status = EFreeTrialStatusType.NO_TRIAL
      delete authObject.trial_end_at
      localStorage.setItem('auth', JSON.stringify(authObject))
      setAuth(authObject)
      onPaymentSuccess()
    }
  }

  async function onSubmit(formValues: z.infer<typeof subscribeFormSchema>) {
    if (!stripe || !elements) {
      return // Ensure Stripe and Elements are loaded
    }

    setLoading(true)
    setError(null) // Clear any previous error

    try {
      // Create new payment method
      const { paymentMethod, error: pmError } =
        // @ts-ignore
        await stripe.createPaymentMethod({
          type: 'card',
          card: elements.getElement('card'),
          billing_details: {
            email: formValues.email,
            name: `${formValues.firstName} ${formValues.lastName}`,
            address: {
              line1: formValues.address,
              city: formValues.city,
              state: formValues.state,
              postal_code: formValues.zip,
            },
          },
        })
      if (pmError) {
        setError(
          pmError.message
            ? pmError.message
            : 'Failed to create payment method. Please try again.'
        )
        setLoading(false)
        return
      }

      const priceId = isDiscount ? tier?.price_discount_id : tier?.price_id
      const tierName = tierKey + (isAnnual ? '-annual': '') + (isDiscount ? '-discount' : '')


      // Call backend to create subscription
      const response = await callAPI('/change_plan', {
        session_key: auth.sessionKey,
        email: formValues.email,
        tier: tierName,
        payment_method_id: paymentMethod.id,
      })

      const { status, msg, clientSecret } = response

      const handleAuth = (userCredential:any) => {
        localStorage.setItem('auth', JSON.stringify(userCredential || {}))
        setAuth(userCredential)
        setIsLoggedIn(!!userCredential)
        saveLocalStorage()
        navigate('/', { replace: true }) // Redirect to home
      }

      switch (status) {
          case 'fail':
            setError(msg)
            break

          case 'payment': {
            try {
              const { error, paymentIntent } = await stripe.confirmCardPayment(
                clientSecret,
                {
                  payment_method: paymentMethod.id,
                }
              )
              if (error) {
                setError('Payment failed. Please try again.')
              } else if (paymentIntent?.status === 'succeeded') {
                response.status = 'success'
                const userCredential = parseProfileResponse(response)
                handleAuth(userCredential)
              } else {
                handleAuth(null) // Clear auth on payment failure
              }
            } catch (err) {
              setError('An unexpected error occurred. Please try again.')
            }
            break
          }

          default: {
            const userCredential = parseProfileResponse(response)
            handleAuth(userCredential)
            break
          }
      } 
    } catch (error) {
      console.error('Error creating subscription:', error)
      setError('Something went wrong. Please try again.')
    } finally {
      setLoading(false) // Reset loading state
    }
  }

  if (!tierKey) return null

  return (
    <>
      <TierCard
        tierKey={tierKey}
        isSubscribe={true}
        isAnnual={isAnnual}
        isDiscount={isDiscount}
      />
      <Separator />
      <div>
        <h2 className="text-2xl font-bold mb-6">Billing Info</h2>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      First Name <span className="text-red-500"> *</span>
                    </FormLabel>
                    <FormControl>
                      <Input className="h-14" placeholder="John" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Last Name
                      <span className="text-red-500"> *</span>
                    </FormLabel>
                    <FormControl>
                      <Input className="h-14" placeholder="Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Email
                    <span className="text-red-500"> *</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      className="h-14"
                      type="email"
                      placeholder="john@email.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Address
                    <span className="text-red-500"> *</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      className="h-14"
                      placeholder="Street Address"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="additionalAddress"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Additional Address</FormLabel>
                  <FormControl>
                    <Input
                      className="h-14"
                      placeholder="Additional Address"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <FormField
                control={form.control}
                name="state"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      State
                      <span className="text-red-500"> *</span>
                    </FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="h-14">
                          <SelectValue placeholder="Select State" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {statesArray.map((state) => (
                          <SelectItem key={state} value={state}>
                            {state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      City
                      <span className="text-red-500"> *</span>
                    </FormLabel>
                    <FormControl>
                      <Input className="h-14" placeholder="City" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="zip"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      ZIP
                      <span className="text-red-500"> *</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="h-14"
                        placeholder="ZIP Code"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Separator />
            <div>
              <h2 className="text-2xl font-bold mb-3">Payment</h2>
              <div className="mb-5 text-charcoal">
                All transactions are secure and encrypted
              </div>
              <div className="mb-5">
                <CardElement className="mb-5" />
                {error && <div style={{ color: 'red' }}>{error}</div>}

                <Separator />
                <div className="mt-5 mb-5">
                  <FormField
                    name="terms"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="relative -top-[5px] text-charcoal">
                          <span>
                            I have read and agreed with{' '}
                            <a
                              target="_blank"
                              rel="noreferrer"
                              href="/terms/Hydra Logic Master Services Agreement.pdf"
                              className="text-primary hover:underline"
                            >
                              Master Service Agreement
                            </a>
                            ,{' '}
                            <a
                              target="_blank"
                              rel="noreferrer"
                              href="https://hydrascore.com/privacy-policy/"
                              className="text-primary hover:underline"
                            >
                              HydraScore Privacy Policy
                            </a>{' '}
                            and{' '}
                            <a
                              target="_blank"
                              rel="noreferrer"
                              href="https://hydrascore.com/terms-and-conditions"
                              className="text-primary hover:underline"
                            >
                              HydraScore Website Terms and Conditions
                            </a>
                            .
                          </span>
                          <FormMessage />
                        </div>
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={!stripe || !elements || loading}
                className="w-full md:w-auto"
              >
                {loading ? 'Processing...' : 'Confirm Payment'}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </>
  )
}
